import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";
import { AlertTriangle, Clock, MapPin, ShieldCheck } from "lucide-react";

const SEVERITY_COLOR = { bassa: "#10B981", media: "#F59E0B", alta: "#EF4444" };
const STATUS_LABEL = { aperta: "Aperta", presa_in_carico: "In lavorazione", chiusa: "Chiusa" };

export default function ReportisticaSicurezza() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reportStats, setReportStats] = useState(null);
  const [checkStats, setCheckStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (from) params.append("date_from", from);
      if (to) params.append("date_to", to);
      const [r1, r2] = await Promise.all([
        api.get(`/reports/security-summary?${params.toString()}`),
        api.get(`/site-checks/summary?${params.toString()}`),
      ]);
      setReportStats(r1.data);
      setCheckStats(r2.data);
    } catch { toast.error("Errore caricamento reportistica"); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const typeChart = reportStats ? Object.entries(reportStats.by_type).map(([name, qty]) => ({ name, qty })) : [];
  const severityChart = reportStats ? Object.entries(reportStats.by_severity).map(([name, qty]) => ({ name, qty })) : [];

  return (
    <div className="space-y-6" data-testid="reportistica-sicurezza-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Cantieri</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Reportistica sicurezza</h1>
        <p className="text-sm text-slate-500 mt-1">Segnalazioni e verifiche in cantiere, dati aggregati</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="Da" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} placeholder="A" />
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!loading && (
        <Tabs defaultValue="segnalazioni">
          <TabsList>
            <TabsTrigger value="segnalazioni">Segnalazioni</TabsTrigger>
            <TabsTrigger value="verifiche">Verifiche in cantiere</TabsTrigger>
          </TabsList>

          <TabsContent value="segnalazioni" className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Totale" value={reportStats?.total ?? 0} icon={ShieldCheck} />
              <Stat label="Aperte" value={reportStats?.by_status?.aperta ?? 0} icon={AlertTriangle} tone="warn" />
              <Stat label="In lavorazione" value={reportStats?.by_status?.presa_in_carico ?? 0} icon={Clock} tone="warn" />
              <Stat label="Tempo medio chiusura" value={reportStats?.avg_close_hours != null ? `${reportStats.avg_close_hours}h` : "—"} icon={Clock} />
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold mb-3">Per tipologia</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={typeChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="qty" fill="#0067b2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold mb-3">Per gravità</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={severityChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="qty" radius={[4, 4, 0, 0]}>
                      {severityChart.map((e) => <Cell key={e.name} fill={SEVERITY_COLOR[e.name] || "#94A3B8"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-500" /> Cantieri con più segnalazioni
                </h3>
                {(!reportStats?.top_sites || reportStats.top_sites.length === 0)
                  ? <p className="text-sm text-slate-500">Nessun dato</p>
                  : <div className="space-y-1.5">
                      {reportStats.top_sites.map(s => (
                        <div key={s.site} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                          <span>{s.site}</span>
                          <Badge variant="outline">{s.count}</Badge>
                        </div>
                      ))}
                    </div>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="verifiche" className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat label="Verifiche effettuate" value={checkStats?.total ?? 0} icon={ShieldCheck} />
              <Stat label="Cantieri verificati" value={checkStats?.by_site?.length ?? 0} icon={MapPin} />
              <Stat label="Tecnici coinvolti" value={checkStats?.by_tecnico?.length ?? 0} icon={Clock} />
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold mb-3">Conformità per voce di controllo</h3>
                <p className="text-xs text-slate-500 mb-3">Percentuale di risposte "Sì" sul totale delle risposte date per ogni voce.</p>
                <div className="space-y-2.5">
                  {(checkStats?.fields || []).filter(f => f.total > 0).map(f => (
                    <div key={f.field}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-600">{f.label}</span>
                        <span className={`font-semibold ${f.pct_ok < 90 ? "text-red-600" : "text-emerald-600"}`}>
                          {f.pct_ok}% · {f.no} No / {f.total}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${f.pct_ok < 90 ? "bg-red-400" : "bg-emerald-500"}`}
                             style={{ width: `${f.pct_ok}%` }} />
                      </div>
                    </div>
                  ))}
                  {(!checkStats?.fields || checkStats.fields.every(f => f.total === 0)) && (
                    <p className="text-sm text-slate-500">Nessuna verifica registrata nel periodo selezionato.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-500" /> Verifiche per cantiere
                </h3>
                {(!checkStats?.by_site || checkStats.by_site.length === 0)
                  ? <p className="text-sm text-slate-500">Nessun dato</p>
                  : <div className="space-y-1.5">
                      {checkStats.by_site.map(s => (
                        <div key={s.site} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                          <span>{s.site}</span>
                          <Badge variant="outline">{s.count}</Badge>
                        </div>
                      ))}
                    </div>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  const tones = { warn: "border-amber-200 bg-amber-50", ok: "border-emerald-200 bg-emerald-50" };
  return (
    <Card className={`border-slate-200 ${tones[tone] || ""}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
          {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        </div>
        <div className="font-display text-2xl font-black">{value}</div>
      </CardContent>
    </Card>
  );
}
